import { useMemo } from 'react';

import { toDiscoveredCluster } from '@cast/features-cluster-shared';
import { CloudAssetCluster, OptimizationMetrics } from '@cast/types-inventory';
import { toNumOrZero, toBig } from '@cast/utils';

import { safeDivide } from 'utils/safeDivide';

import { getPercentage } from '../../SummarySection/utils';
import {
  ClusterRowModel,
  ClusterRowDisplayMetrics,
  SummarySectionMetrics,
  IssuesDrawerMetrics,
} from '../../types';
import {
  getCostPerMonth,
  getEfficiencyPercent,
  toCalcMemoryUnit,
} from '../../utils';
import { useDiscoveredClusters } from '../useDiscoveredClusters';

/*
  Recursively count occurrences of `state: "POOR"` and `state: "CONCERNING"` within the object
  If prop name is `next` or `prev`, skip it
 */
const getIssuesCount = (item?: object) => {
  if (!item) {
    return 0;
  }

  let count = 0;

  const traverse = (obj: any) => {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    // Check if current object has a state property with POOR or CONCERNING
    if (obj.state === 'POOR' || obj.state === 'CONCERNING') {
      count++;
    }

    // Recursively traverse nested objects
    for (const key in obj) {
      // Skip 'next' and 'prev' properties
      if (key === 'next' || key === 'prev') {
        continue;
      }
      const value = obj[key];
      if (typeof value === 'object' && value !== null) {
        traverse(value);
      }
    }
  };

  traverse(item);
  return count;
};

const calculateCosts = (cluster: CloudAssetCluster) => {
  const current = cluster.optimization?.metrics;
  const optimized = cluster.optimization?.optimizationMetrics;

  const getCostByType = (
    type: 'provisioned' | 'requested',
    metrics?: OptimizationMetrics
  ) => {
    let cost = toBig(0);

    if (metrics?.cpu) {
      cost = cost.add(
        toBig(
          getCostPerMonth(
            metrics?.cpu?.[type]?.amount,
            metrics?.cpu?.[type]?.costPerUnit
          )
        )
      );
    }

    if (metrics?.memory) {
      cost = cost.add(
        toBig(
          getCostPerMonth(
            metrics?.memory?.[type]?.amount,
            metrics?.memory?.[type]?.costPerUnit
          )
        )
      );
    }

    if (metrics?.gpu) {
      cost = cost.add(
        toBig(
          getCostPerMonth(
            metrics?.gpu?.[type]?.amount,
            metrics?.gpu?.[type]?.costPerUnit
          )
        )
      );
    }

    return cost;
  };

  // Provisioned costs represent the infrastructure costs
  const currentProvisionedCost = getCostByType('provisioned', current);
  const optimizedProvisionedCost = getCostByType('provisioned', optimized);

  // Requested costs represent the workload costs
  const currentRequestedCost = getCostByType('requested', current);
  const optimizedRequestedCost = getCostByType('requested', optimized);

  // Savings how much node autoscaler can save
  const provisionedSavings = currentProvisionedCost.sub(
    optimizedProvisionedCost
  );
  const provisionedSavingsPercentage =
    safeDivide(provisionedSavings, currentProvisionedCost) * 100;

  // Savings how much workload autoscaler can save
  const requestedSavings = currentRequestedCost.sub(optimizedRequestedCost);
  const requestedSavingsPercentage =
    safeDivide(requestedSavings, currentRequestedCost) * 100;

  return {
    currentProvisionedCost,
    optimizedProvisionedCost,
    currentRequestedCost,
    optimizedRequestedCost,
    provisionedSavings,
    provisionedSavingsPercentage,
    requestedSavings,
    requestedSavingsPercentage,
  };
};

type CostsSummary = Exclude<ReturnType<typeof calculateCosts>, undefined>;

const toDisplayMetrics = ({
  cluster: { optimization },
  costsSummary,
}: {
  cluster: CloudAssetCluster;
  costsSummary?: CostsSummary;
}): ClusterRowDisplayMetrics => {
  const current = optimization?.metrics;
  const optimal = optimization?.optimizationMetrics;

  const provisionedCpu = toNumOrZero(current?.cpu?.provisioned?.amount);
  const requestedCpu = toNumOrZero(current?.cpu?.requested?.amount);

  const optimalProvisionedCpus = toNumOrZero(optimal?.cpu?.provisioned?.amount);
  const cpuReduction = provisionedCpu - optimalProvisionedCpus;

  const provisionedMemory = toCalcMemoryUnit(current?.memory?.provisioned);
  const requestedMemory = toCalcMemoryUnit(current?.memory?.requested);

  const issuesCount = getIssuesCount(
    optimization?.clusterScore?.resourceUtilization
  );

  const totalSavings = Math.max(
    toNumOrZero(costsSummary?.provisionedSavings.toNumber()),
    0
  );

  const wasteReductionPercentage = getPercentage(cpuReduction, provisionedCpu);

  return {
    // Granular resource metrics for the table columns, when Cloud Asset data
    // provides them. Merged with tag-based metrics in useClusterListRowModels.
    resources: current
      ? {
          cpu: {
            allocatable: provisionedCpu,
            used: toNumOrZero(current.cpu?.used?.amount),
            requested: requestedCpu,
          },
          memory: {
            allocatable: provisionedMemory,
            used: toCalcMemoryUnit(current.memory?.used),
            requested: requestedMemory,
          },
        }
      : undefined,
    efficiency: {
      cpu: {
        provisioned: provisionedCpu,
        requested: requestedCpu,
        efficiencyPercent: getEfficiencyPercent(requestedCpu, provisionedCpu),
      },
      memory: {
        provisioned: provisionedMemory,
        requested: requestedMemory,
        efficiencyPercent: getEfficiencyPercent(
          requestedMemory,
          provisionedMemory
        ),
      },
    },
    reduceWaste: {
      percentage: wasteReductionPercentage,
      cpuCount: cpuReduction,
      optimal: !totalSavings && !wasteReductionPercentage,
    },
    save: totalSavings,
    score: undefined, // TODO: Add when BE is ready - cluster.optimization?.clusterScore
    issues: issuesCount,
  };
};

const toSummarySectionRowItem = ({
  cluster: { optimization },
  costsSummary,
}: {
  cluster: CloudAssetCluster;
  costsSummary: CostsSummary;
}): SummarySectionMetrics => {
  const current = optimization?.metrics;
  const optimized = optimization?.optimizationMetrics;

  const currentlyRequestedCpuCores = toNumOrZero(
    current?.cpu?.requested?.amount
  );

  const currentlyProvisionedCpuCores = toNumOrZero(
    current?.cpu?.provisioned?.amount
  );

  return {
    requestedCpuCores: currentlyRequestedCpuCores,
    provisionedCpuCores: currentlyProvisionedCpuCores,
    usedCpuCores: toNumOrZero(current?.cpu?.used?.amount),
    downscaleWorkloadCpuCores: toBig(currentlyRequestedCpuCores)
      .sub(toBig(optimized?.cpu?.requested?.amount))
      .toNumber(),
    downscaleNodeCpuCores: toBig(currentlyProvisionedCpuCores)
      .sub(toBig(optimized?.cpu?.provisioned?.amount))
      .toNumber(),
    currentMonthlyCost: costsSummary.currentProvisionedCost.toNumber(),
    optimizedMonthlyCost: costsSummary.optimizedProvisionedCost.toNumber(),
  };
};

const toIssuesDrawerModel = ({
  cluster: { optimization },
  costsSummary,
}: {
  cluster: CloudAssetCluster;
  costsSummary: CostsSummary;
}): IssuesDrawerMetrics => {
  return {
    score: optimization?.clusterScore,
    savingsMonthly: {
      total: costsSummary.provisionedSavings.toNumber(),
      workloadAutoscaler: costsSummary.requestedSavings.toNumber(),
      nodeAutoscaler: costsSummary.provisionedSavings.toNumber(),
    },
  };
};

const discoveredClusterToRowModel = (
  cluster: CloudAssetCluster
): ClusterRowModel | undefined => {
  const base = toDiscoveredCluster(cluster);
  if (!base) {
    return undefined;
  }

  const costsSummary = calculateCosts(cluster);
  const summaryMetrics = toSummarySectionRowItem({ cluster, costsSummary });

  return {
    ...base,
    isDiscovered: true,
    isManaged: false,
    displayMetrics: toDisplayMetrics({
      cluster,
      costsSummary,
    }),
    summaryMetrics,
    issuesDrawerMetrics: toIssuesDrawerModel({
      cluster,
      costsSummary,
    }),
    error: cluster.error,
  };
};

export const useDiscoveredClusterRowModels = () => {
  const {
    isLoading,
    refetch,
    error,
    discoveredClusters: _discoveredClusters,
  } = useDiscoveredClusters();

  const discoveredClusters = useMemo(() => {
    return _discoveredClusters || [];
  }, [_discoveredClusters]);

  const discoveredClusterRowModels = useMemo(
    () =>
      discoveredClusters
        .map(discoveredClusterToRowModel)
        .filter((row): row is ClusterRowModel => row != null),
    [discoveredClusters]
  );

  return {
    isLoading,
    refetch,
    error,
    discoveredClusterRowModels,
  };
};
